/**
 * Place a tool-prep subject onto the editor canvas.
 * `placement` is fractions of the preview stage (full image frame) plus the
 * source crop the bounding box was showing.
 */
export function commitSubjectPlacement(layout, placement, canvasWidth, canvasHeight) {
  const cw = Number(canvasWidth);
  const ch = Number(canvasHeight);
  const orig = layout.originalImageSizeRef.current;
  if (!cw || !ch || !orig?.width || !orig?.height) return false;
  if (!(placement?.width > 0) || !(placement?.height > 0)) return false;

  const frame = {
    x: cw * (Number(placement.x) || 0),
    y: ch * (Number(placement.y) || 0),
    width: cw * placement.width,
    height: ch * placement.height,
  };
  if (!(frame.width > 0) || !(frame.height > 0)) return false;

  layout.layoutLockRef.current = true;
  layout.layoutLockFullObjectRef.current = {
    width: frame.width,
    height: frame.height,
    x: frame.x,
    y: frame.y,
    imageW: orig.width,
    imageH: orig.height,
    // Subject-only resize. A later, different image may drop this lock.
    // Preset / padding locks (defer) stay put.
    fromSubjectPlacement: placement.defer !== true,
  };
  if (layout.canvasSizeRef) {
    layout.canvasSizeRef.current = { width: cw, height: ch };
  }

  const crop = placement.crop;
  const cropMatchesImage =
    crop &&
    crop.width > 0 &&
    crop.height > 0 &&
    Math.abs(crop.imageWidth - orig.width) < 2 &&
    Math.abs(crop.imageHeight - orig.height) < 2;

  if (cropMatchesImage) {
    const sx = frame.width / crop.imageWidth;
    const sy = frame.height / crop.imageHeight;
    const nextCrop = {
      x: crop.x,
      y: crop.y,
      width: crop.width,
      height: crop.height,
    };
    const nextObjectSize = {
      width: crop.width * sx,
      height: crop.height * sy,
    };
    const nextObjectPosition = {
      x: frame.x + crop.x * sx,
      y: frame.y + crop.y * sy,
    };
    layout.cropStateRef.current = nextCrop;
    layout.cropExtentRef.current = { ...nextCrop };
    layout.objectSizeRef.current = nextObjectSize;
    layout.objectPositionRef.current = nextObjectPosition;
    layout.objectTightBoundsRef.current = true;
    layout.setCropState(nextCrop);
    layout.setObjectSize(nextObjectSize);
    layout.setObjectPosition(nextObjectPosition);
  } else {
    layout.objectSizeRef.current = { width: frame.width, height: frame.height };
    layout.objectPositionRef.current = { x: frame.x, y: frame.y };
    layout.setObjectSize({ width: frame.width, height: frame.height });
    layout.setObjectPosition({ x: frame.x, y: frame.y });
    const fullCrop = { x: 0, y: 0, width: orig.width, height: orig.height };
    layout.cropStateRef.current = fullCrop;
    layout.cropExtentRef.current = { ...fullCrop };
    layout.setCropState(fullCrop);
    layout.objectTightBoundsRef.current = false;
    if (typeof layout.retighten === "function") {
      layout.retighten();
    }
  }

  if (placement.applyRotation !== false && layout.setObjectRotation) {
    const rotation = Number(placement.rotation) || 0;
    if (layout.objectRotationRef) {
      layout.objectRotationRef.current = rotation;
    }
    layout.setObjectRotation(rotation);
  }

  return true;
}

export function consumePendingSubjectPlacement(
  layout,
  { force = false, canvasWidth, canvasHeight } = {}
) {
  const pending = layout.pendingSubjectPlacementRef?.current;
  if (!pending) return false;
  if (pending.defer && !force) return false;
  const cw = canvasWidth || layout.canvasSizeRef.current?.width;
  const ch = canvasHeight || layout.canvasSizeRef.current?.height;
  const ok = commitSubjectPlacement(layout, pending, cw, ch);
  if (ok) layout.pendingSubjectPlacementRef.current = null;
  return ok;
}
